<script>
  /**
   * BulkDeleteModal — confirm, run, and report a batch of deletes.
   *
   * There is no bulk delete endpoint, so the caller supplies a single-item
   * `onDelete` and this component runs it once per item, sequentially, with a
   * determinate progress bar. Failures never abort the run; they are collected
   * and reported at the end.
   *
   * The confirm heading arrives pre-formatted via `title` rather than as an i18n
   * key, so callers can use ICU plurals that the unit-test i18n mock cannot render.
   *
   * @example
   * <BulkDeleteModal
   *   bind:show={showBulkDelete}
   *   items={selectedAssets}
   *   title={$format('label.BULK_DELETE.CONFIRM_IMAGES', { values: { count: selectedAssets.length } })}
   *   onDelete={(item) => AssetsService.deleteImage(item)}
   *   onFinish={onBulkDeleteFinish}
   * />
   */

  import { format } from 'svelte-i18n';
  import { Alert, Button, Modal, Progress } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { NavigationGuard } from '@client/components/navigation-guard';

  /**
   * Controls whether the modal is open. Use `bind:show`.
   * @type {boolean}
   */
  export let show = false;

  /**
   * The items to delete. Read once when the run starts.
   * @type {Object[]}
   */
  export let items = [];

  /**
   * Already-formatted confirm heading, e.g. "Delete 3 images?".
   * @type {string}
   */
  export let title = '';

  /**
   * Deletes a single item. A resolved value with `status >= 400`, or a thrown
   * error, counts as a failure.
   * @type {(item: Object) => Promise<any>}
   */
  export let onDelete = async () => {};

  /**
   * Called once when the outcome is dismissed, with the items that failed.
   * An empty array means everything succeeded.
   * @type {(failed: Object[]) => void}
   */
  export let onFinish = () => {};

  /** @type {'confirm' | 'deleting' | 'results'} */
  let phase = 'confirm';

  /** How many items have been attempted so far. */
  let completed = 0;

  /** @type {Object[]} */
  let failed = [];

  const reset = () => {
    phase = 'confirm';
    completed = 0;
    failed = [];
  };

  /** Best-effort display name for an asset. */
  const getName = (item) => item.rulename || item.filename || item.title || '';

  const finish = () => {
    onFinish(failed);
    show = false;
  };

  const cancel = () => {
    show = false;
  };

  const run = async () => {
    phase = 'deleting';
    completed = 0;
    failed = [];

    for (const item of items) {
      try {
        const response = await onDelete(item);

        if (response?.status >= 400) {
          failed = [...failed, item];
        }
      } catch {
        failed = [...failed, item];
      }

      completed += 1;
    }

    if (failed.length) {
      phase = 'results';
      return;
    }

    finish();
  };

  // The X and Escape are inert mid-run so a delete loop can't be orphaned.
  const toggle = () => {
    if (phase === 'deleting') {
      return;
    }

    if (phase === 'results') {
      finish();
      return;
    }

    cancel();
  };

  $: total = items.length;
  $: percent = total ? Math.ceil((completed / total) * 100) : 0;
  $: succeeded = completed - failed.length;
  $: show === false && reset();
</script>

<!-- Outside the Modal: sveltestrap portals its slot, and the guard must stay mounted regardless of portal timing. -->
<NavigationGuard when={phase === 'deleting'} message={$format('label.BULK_DELETE.CONFIRM_LEAVE')} />

<Modal bind:isOpen={show} size="sm" {toggle} keyboard={phase !== 'deleting'}>
  <div class="p-4">
    {#if phase === 'confirm'}
      <h1 class="h5 mb-2">{title}</h1>
      <p class="text-muted">{$format('label.BULK_DELETE.WARNING')}</p>

      <div class="d-flex justify-content-end gap-2">
        <Button color="secondary" outline on:click={cancel}>{$format('label.CANCEL')}</Button>
        <Button color="danger" on:click={run}>{$format('label.DELETE')}</Button>
      </div>
    {:else if phase === 'deleting'}
      <Progress value={percent} />

      <p class="text-center mt-3 mb-0" data-testid="bulk-delete-progress">
        {$format('label.BULK_DELETE.PROGRESS', { values: { n: completed, total } })}
      </p>
    {:else}
      {#if succeeded}
        <Alert color="success" class="d-flex align-items-center">
          <Icon name="check-circle" class="me-2" />
          {$format('label.BULK_DELETE.SUCCEEDED', { values: { count: succeeded, total } })}
        </Alert>
      {/if}

      <Alert color="danger">
        <div class="d-flex align-items-center">
          <Icon name="alert-triangle" class="me-2" />
          {$format('label.BULK_DELETE.FAILED', { values: { count: failed.length } })}
        </div>

        <ul class="mb-0 mt-2 ps-4" data-testid="bulk-delete-failures">
          {#each failed as item}
            <li>{getName(item)}</li>
          {/each}
        </ul>
      </Alert>

      <div class="d-flex justify-content-end">
        <Button color="secondary" on:click={finish}>{$format('label.CLOSE')}</Button>
      </div>
    {/if}
  </div>
</Modal>
